import * as yup from "yup";
import { patientService } from "@/services";


const emailSchema = yup.string().email();
/**
 * taken from here
 * {@link https://www.regular-expressions.info/email.html}
 *
 * search for "We get a more practical implementation of RFC 5322 \
 * if we omit IP addresses, domain-specific addresses, the syntax \
 * using double quotes and square brackets. It will still match \
 * 99.99% of all email addresses in actual use today."
 *
 * 99.99% percent seems good enough
 */
const emailRegex = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

/**
 * default Yup email schema for some reason treats\
 * `"priyarajesh.hsstest+qrm232@gmail"` as valid email,\
 * because of that we use `"emailRegex"` that should \
 * have some pretty great accuracy (as the article\
 * states) in combination with default yup validation.\
 * Only if both approve provided value - then we treat\
 * it as valid one
 */
export const emailOrEmptyStringSchema = (
  yup.string()
    .defined()
    .test({
      name: "isValidEmailOrIsEmpty",
      message: "Invalid email address",
      test: v => {
        if (v === "") {
          return true;
        }

        try {
          if (v.match(emailRegex) === null) return false;

          emailSchema.validateSync(v);
          return true;
        } catch (e) {
          if (e instanceof yup.ValidationError) {
            return false;
          }

          throw e;
        }
      },
    })
);

export const createEmailSchemaWithDuplicateCheck = (currentPatientId?: string) => {
  let lastCheckedValue: string | null = null;
  let cachedResult = true;
  const checkResults = new Map<string, Promise<boolean>>();
  let debounceTimer: NodeJS.Timeout | null = null;
  let pendingResolve: ((result: boolean) => void) | null = null;
  let pendingEmail: string | null = null;

  const checkEmailDuplicate = async (email: string): Promise<boolean> => {
    try {
      const existingPatients = await patientService.getList({ email });

      if (existingPatients?.data?.length > 0) {
        // If we're editing and the email belongs to the current patient, allow it
        if (currentPatientId && existingPatients.data.some((p: any) => p.id === currentPatientId)) {
          return true;
        }
        // Otherwise, it's a duplicate
        return false;
      }

      return true;
    } catch (error) {
      // If we can't check, allow the email (don't block on API errors)
      console.warn("Could not check for duplicate email:", error);
      return true;
    }
  };

  const executeCheck = async (email: string) => {
    // Cancel any pending debounce
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }

    console.log("Running duplicate check for:", email);
    const result = await checkEmailDuplicate(email);
    cachedResult = result;
    lastCheckedValue = email;
    
    // Resolve all pending promises for this email
    const pendingPromise = checkResults.get(email);
    if (pendingPromise) {
      checkResults.delete(email);
    }
    
    // If there's a pending resolve for this email, call it
    if (pendingResolve && pendingEmail === email) {
      pendingResolve(result);
      pendingResolve = null;
      pendingEmail = null;
    }
  };

  return emailOrEmptyStringSchema
    .test({
      name: "checkDuplicateEmail",
      message: "This email is already associated with another patient. Please link to the existing patient instead.",
      test: function (value, context) {
        if (!value || value === "") {
          // Clear any pending checks when email is cleared
          if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
          }
          pendingResolve = null;
          pendingEmail = null;
          return true;
        }

        // If this value was already checked, return the cached result
        if (value === lastCheckedValue) {
          return cachedResult;
        }

        // Check if we already have a pending check for this email
        const existingCheck = checkResults.get(value);
        if (existingCheck) {
          return existingCheck;
        }

        // Only validate if email looks reasonably complete (has @ and at least one character after @)
        // This prevents checking incomplete emails like "user@" or "user@d"
        const emailParts = value.split('@');
        if (emailParts.length !== 2 || emailParts[1].length < 2 || !emailParts[1].includes('.')) {
          // Email is incomplete, return true (valid for now) but don't check yet
          return true;
        }

        // Create a new promise for this check
        const checkPromise = new Promise<boolean>((resolve) => {
          // Cancel any previous debounce if the email changed
          if (debounceTimer && pendingEmail !== value) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
            // Resolve previous pending check with true to avoid blocking validation
            if (pendingResolve && pendingEmail) {
              pendingResolve(true);
            }
          }

          // Set up new pending check
          pendingResolve = resolve;
          pendingEmail = value;

          // Debounce the actual API call
          debounceTimer = setTimeout(() => {
            executeCheck(value);
          }, 500);
        });

        checkResults.set(value, checkPromise);
        return checkPromise;
      },
    });
};

